import markerSDK from "@marker.io/browser";
import { FC, Suspense, useContext } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  Outlet,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";

import type { Theme } from "@mui/material";
import { useMediaQuery } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import { styled } from "@mui/material/styles";

import {
  captureProductEvent,
  productAnalyticsEvents,
} from "../../analytics/events";
import Banner from "../../components/Banner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { UserContext } from "../../contexts/UserContext";
import useFeatureFlag from "../../hooks/use-feature-flag";
import { useIsHostedCluster } from "../../hooks/use-is-hosted-cluster";
import useServerNotifications from "../../hooks/use-server-notifications";
import { useSettings } from "../../hooks/use-settings";
import { useSelector } from "../../store";
import {
  useGetVersionQuery,
  useListOrganizationsQuery,
} from "../../store/pomeriumApi";
import { MobileNav } from "./mobile-nav";
import { SideNav } from "./side-nav";
import { TopNav } from "./top-nav";

const SIDE_NAV_WIDTH = 270;
const SIDE_NAV_PINNED_WIDTH = 73;
const TOP_NAV_HEIGHT = 64;

const useMobileNav = () => {
  const [open, setOpen] = useState<boolean>(false);
  const { pathname } = useLocation();

  const handlePathnameChange = useCallback((): void => {
    if (open) {
      setOpen(false);
    }
  }, [open]);

  useEffect(() => {
    handlePathnameChange();
  }, [pathname]);

  const handleOpen = useCallback((): void => {
    setOpen(true);
  }, []);

  const handleClose = useCallback((): void => {
    setOpen(false);
  }, []);

  return {
    handleClose,
    handleOpen,
    open,
  };
};

const LayoutRoot = styled("div")(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  display: "flex",
  flex: "1 1 auto",
  maxWidth: "100%",
  paddingTop: TOP_NAV_HEIGHT,
  [theme.breakpoints.up("lg")]: {
    paddingLeft: SIDE_NAV_WIDTH,
  },
}));

const LayoutContainer = styled("div")({
  display: "flex",
  flex: "1 1 auto",
  flexDirection: "column",
  width: "100%",
});

const initializeHelpMenu = markerSDK.loadWidget({
  project: "675a660099243a0800fc81d6",
});

const Layout: FC = () => {
  const navigate = useNavigate();
  const mdDown = useMediaQuery((theme: Theme) => theme.breakpoints.down("lg"));
  const settings = useSettings();
  const mobileNav = useMobileNav();
  const { user } = useContext(UserContext);
  const { organizationId, clusterId, userId } = useSelector(
    (state) => state.current,
  );

  const { data: apiVersion } = useGetVersionQuery();
  useServerNotifications(organizationId);

  // Hosted cluster banner state
  const isHostedCluster = useIsHostedCluster();
  const [hostedBannerDismissed, setHostedBannerDismissed] = useState<boolean>(
    () => {
      return localStorage.getItem(`${userId}_hostedBannerDismissed`) === "true";
    },
  );

  const handleDismissHostedBanner = useCallback((): void => {
    localStorage.setItem(`${userId}_hostedBannerDismissed`, "true");
    setHostedBannerDismissed(true);
  }, [userId]);

  // MCP banner state
  const showMcpBanner = useFeatureFlag("mcp-pomerium-app-ui-banner", false);
  const [mcpBannerDismissed, setMcpBannerDismissed] = useState<boolean>(() => {
    return localStorage.getItem(`${userId}_mcpBannerDismissed`) === "true";
  });

  const handleDismissMcpBanner = useCallback((): void => {
    captureProductEvent(productAnalyticsEvents.mcpBannerDismissed);
    localStorage.setItem(`${userId}_mcpBannerDismissed`, "true");
    setMcpBannerDismissed(true);
  }, [userId]);

  const [mcpModalOpen, setMcpModalOpen] = useState(false);

  const mcpBannerViewedRef = useRef(false);

  useEffect(() => {
    if (showMcpBanner && !mcpBannerDismissed && !mcpBannerViewedRef.current) {
      mcpBannerViewedRef.current = true;
      captureProductEvent(productAnalyticsEvents.mcpBannerViewed);
    }
  }, [showMcpBanner, mcpBannerDismissed]);

  const handleMcpLearnMore = useCallback((): void => {
    captureProductEvent(productAnalyticsEvents.mcpBannerLearnMoreClicked);
    setMcpModalOpen(true);
  }, []);

  // check if trying to access org when user hasn't joined it
  const { data: organizations, isLoading: isOrgsLoading } =
    useListOrganizationsQuery();
  const [searchParams] = useSearchParams();
  useEffect(() => {
    if (
      searchParams.has("oid") &&
      !isOrgsLoading &&
      !organizations?.find((org) => org.id === searchParams.get("oid"))
    ) {
      navigate(`/app/org-access-denied?${searchParams.toString()}`);
    }
  }, [searchParams]);

  useEffect(() => {
    async function updateWidget() {
      const widget = await initializeHelpMenu;
      widget.setReporter({
        email: user?.email || "",
        fullName: user?.displayName || "",
      });
      widget.setCustomData({
        organizationId: organizationId,
        clusterId: clusterId,
        apiVersion: apiVersion?.version || "",
        uiVersion: import.meta.env.VITE_UI_GIT_SHA || "",
      });
    }
    updateWidget();
  }, [
    organizationId,
    clusterId,
    user?.email,
    user?.displayName,
    apiVersion?.version,
  ]);

  const handleNavPin = useCallback((): void => {
    settings.handleUpdate({
      pinNav: !settings.pinNav,
    });
  }, [settings]);

  const offset = settings.pinNav ? SIDE_NAV_WIDTH : SIDE_NAV_PINNED_WIDTH;

  return (
    <>
      <TopNav onNavOpen={mobileNav.handleOpen} openNav={mobileNav.open} />
      {mdDown && (
        <MobileNav onClose={mobileNav.handleClose} open={mobileNav.open} />
      )}
      {!mdDown && <SideNav onPin={handleNavPin} pinned={!!settings.pinNav} />}
      <LayoutRoot
        sx={{
          pl: {
            lg: offset + "px",
          },
        }}
      >
        <LayoutContainer>
          <Suspense fallback={<CircularProgress sx={{ mx: "auto", my: 10 }} />}>
            {isHostedCluster && !hostedBannerDismissed && (
              <div className="flex place-content-center">
                <Banner
                  text={
                    <>
                      Hosted clusters for Local MCP development.{" "}
                      <a
                        href="https://www.pomerium.com/docs/guides/local-mcp"
                        target="_blank"
                        rel="noreferrer"
                        className="underline"
                      >
                        Learn more
                      </a>
                    </>
                  }
                  onClose={handleDismissHostedBanner}
                />
              </div>
            )}
            {showMcpBanner && !mcpBannerDismissed && (
              <div className="flex place-content-center">
                <Banner
                  text="The Pomerium MCP server is now available."
                  ctaAction={handleMcpLearnMore}
                  ctaActionTitle="Learn more"
                  onClose={handleDismissMcpBanner}
                />
              </div>
            )}
            <Dialog open={mcpModalOpen} onOpenChange={setMcpModalOpen}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Pomerium MCP Server</DialogTitle>
                  <DialogDescription>
                    Configure your MCP client with the URL{" "}
                    <a
                      href="https://mcp.pomerium.app"
                      target="_blank"
                      rel="noreferrer"
                      className="underline text-primary"
                    >
                      https://mcp.pomerium.app
                    </a>
                    . Use the HTTP Streamable transport. OAuth is enabled, so
                    make sure to check off or enable OAuth if your client asks.
                  </DialogDescription>
                </DialogHeader>
              </DialogContent>
            </Dialog>
            <Outlet />
          </Suspense>
        </LayoutContainer>
      </LayoutRoot>
    </>
  );
};

export default Layout;
